// 전역 모달 스택 — 중첩 모달에서 Esc 가 "가장 최근(최상단) 모달만" 닫히게 하는 장치.
// 모달이 열리면 push, 닫히면 pop. Esc 키는 자기 id 가 스택 꼭대기일 때만 반응한다.
// (이전에는 열린 모달 전부가 document keydown 을 각자 듣고 동시에 닫혀, 중첩 상태에서
//  Esc 한 번에 모달이 다 사라졌다 — 2026-07-15 수정)
import { useEffect, useRef } from "react";

let stack: number[] = [];
let seq = 0;

function pushModal(): number {
  seq += 1;
  stack.push(seq);
  return seq;
}

function popModal(id: number) {
  stack = stack.filter((x) => x !== id);
}

function isTopModal(id: number) {
  return stack.length > 0 && stack[stack.length - 1] === id;
}

/**
 * open 인 동안 모달 스택에 등록하고, Esc 는 이 모달이 최상단일 때만 onClose 를 부른다.
 * onClose 는 ref 로 들고 있어 매 렌더 새 함수여도 스택 순서가 흔들리지 않는다
 * (effect 의존성에 onClose 를 넣으면 하위 모달 재렌더 때 pop→push 로 순서가 뒤집힌다).
 */
export function useModalEsc(open: boolean, onClose: () => void) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const id = pushModal();
    const handle = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || !isTopModal(id)) return;
      // flatpickr 캘린더가 열려 있으면 Esc 는 캘린더만 닫는다 — 모달까지 닫지 않는다.
      // flatpickr 는 Esc 를 스스로 처리하지 않으므로 인스턴스(input._flatpickr)를 찾아 직접 닫는다.
      if (document.querySelector(".flatpickr-calendar.open")) {
        for (const input of Array.from(document.querySelectorAll("input"))) {
          const fp = (input as HTMLInputElement & { _flatpickr?: { isOpen: boolean; close: () => void } })._flatpickr;
          if (fp?.isOpen) fp.close();
        }
        return;
      }
      onCloseRef.current();
    };
    document.addEventListener("keydown", handle);
    return () => {
      document.removeEventListener("keydown", handle);
      popModal(id);
    };
  }, [open]);
}
